import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSyncSurveyCompetitorPrices } from '../services/survey/survey.mutation';
import {
    showLoadingNotification,
    updateSuccessNotification,
    updateFailedNotification,
} from '../notifications';
import {
    PriceSurvey,
    PriceSurveyCompetitor,
    Competitor,
} from '../codegen/graphql';

export interface SurveyPriceCell {
    priceId: string;
    surveyId: string;
    surveyItemId: string;
    competitorId: string;
    competitor: Competitor;
    priceSurveyCompetitorId: string;
    originalPrice: number | null;
    currentPrice: number | null;
    originalIsNA: boolean;
    isNA: boolean;
    hasChanged: boolean;
}

export interface CellUpdate {
    price?: number | null;
    isNA?: boolean;
}

interface CompetitorTracking {
    activeCompetitors: PriceSurveyCompetitor[];
    pendingAdds: Set<string>;
    pendingRemoves: Set<string>;
}

export interface UseSurveyPricesReturn {
    pricesMap: Map<string, SurveyPriceCell>;
    activeCompetitors: PriceSurveyCompetitor[];
    getCell: (priceId: string) => SurveyPriceCell | undefined;
    getCellByItemAndCompetitor: (
        surveyItemId: string,
        competitorId: string,
    ) => SurveyPriceCell | undefined;
    updateCell: (priceId: string, update: CellUpdate) => void;
    addCompetitorLocally: (competitor: Competitor) => void;
    removeCompetitorLocally: (competitorId: string) => void;
    hasAnyChanges: boolean;
    changedCount: number;
    saveChanges: () => Promise<boolean>;
    isSaving: boolean;
    initializePrices: (survey: PriceSurvey) => void;
    requestReinit: () => void;
}

const SAVE_NOTIFICATION_ID = 'survey-prices-save';

function generateTempId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function useSurveyPrices(
    survey: PriceSurvey | null | undefined,
    onSaveComplete?: () => void | Promise<void>,
): UseSurveyPricesReturn {
    const [pricesMap, setPricesMap] = useState<Map<string, SurveyPriceCell>>(
        new Map(),
    );
    const [competitorTracking, setCompetitorTracking] =
        useState<CompetitorTracking>({
            activeCompetitors: [],
            pendingAdds: new Set(),
            pendingRemoves: new Set(),
        });
    const [isSaving, setIsSaving] = useState(false);

    const surveyRef = useRef<PriceSurvey | null | undefined>(null);
    const reinitRequestedRef = useRef(true);
    const lastInitializedSurveyIdRef = useRef<string | null>(null);
    const isSavingRef = useRef(false);
    const saveInFlightRef = useRef<Promise<boolean> | null>(null);
    const competitorTrackingRef = useRef(competitorTracking);
    const pricesMapRef = useRef(pricesMap);

    useEffect(() => {
        competitorTrackingRef.current = competitorTracking;
    }, [competitorTracking]);

    useEffect(() => {
        pricesMapRef.current = pricesMap;
    }, [pricesMap]);

    const [syncCompetitorPrices] = useSyncSurveyCompetitorPrices();

    const itemCompetitorToPriceId = useMemo(() => {
        const map = new Map<string, string>();
        pricesMap.forEach((cell, priceId) => {
            const key = `${cell.surveyItemId}_${cell.competitorId}`;
            map.set(key, priceId);
        });
        return map;
    }, [pricesMap]);

    const initializePrices = useCallback((surveyData: PriceSurvey) => {
        const newMap = new Map<string, SurveyPriceCell>();

        const compLookup = new Map<string, PriceSurveyCompetitor>();
        surveyData.surveyCompetitors?.forEach(sc => {
            compLookup.set(sc.id, sc);
        });

        surveyData.surveyItems?.forEach(item => {
            item.surveyItemPrices?.forEach(sip => {
                const sc = compLookup.get(sip.priceSurveyCompetitorId || '');

                if (sc?.competitor && sip.id) {
                    const isNA = sip.isNA ?? false;
                    const price = sip.price ?? null;

                    newMap.set(sip.id, {
                        priceId: sip.id,
                        surveyId: surveyData.id,
                        surveyItemId: item.id,
                        competitorId: sc.competitorId,
                        competitor: sc.competitor,
                        priceSurveyCompetitorId:
                            sip.priceSurveyCompetitorId || '',
                        originalPrice: price,
                        currentPrice: price,
                        originalIsNA: isNA,
                        isNA,
                        hasChanged: false,
                    });
                }
            });
        });

        setPricesMap(newMap);

        setCompetitorTracking({
            activeCompetitors: surveyData.surveyCompetitors ?? [],
            pendingAdds: new Set(),
            pendingRemoves: new Set(),
        });

        surveyRef.current = surveyData;
        lastInitializedSurveyIdRef.current = surveyData.id;
    }, []);

    useEffect(() => {
        if (survey) {
            const surveyIdChanged =
                lastInitializedSurveyIdRef.current !== null &&
                lastInitializedSurveyIdRef.current !== survey.id;

            if (reinitRequestedRef.current || surveyIdChanged) {
                reinitRequestedRef.current = false;
                initializePrices(survey);
            }
        }
    }, [survey, initializePrices]);

    const requestReinit = useCallback(() => {
        reinitRequestedRef.current = true;
    }, []);

    const getCell = useCallback(
        (priceId: string): SurveyPriceCell | undefined => {
            return pricesMap.get(priceId);
        },
        [pricesMap],
    );

    const getCellByItemAndCompetitor = useCallback(
        (
            surveyItemId: string,
            competitorId: string,
        ): SurveyPriceCell | undefined => {
            const key = `${surveyItemId}_${competitorId}`;
            const priceId = itemCompetitorToPriceId.get(key);
            return priceId ? pricesMap.get(priceId) : undefined;
        },
        [pricesMap, itemCompetitorToPriceId],
    );

    const updateCell = useCallback((priceId: string, update: CellUpdate) => {
        setPricesMap(prev => {
            const cell = prev.get(priceId);
            if (!cell) {
                return prev;
            }

            let newPrice = cell.currentPrice;
            let newIsNA = cell.isNA;

            if ('price' in update) {
                newPrice = update.price ?? null;
                if (newPrice !== null) {
                    newIsNA = false;
                }
            }

            if ('isNA' in update) {
                newIsNA = update.isNA ?? false;
                if (newIsNA) {
                    newPrice = null;
                }
            }

            const hasChanged =
                newPrice !== cell.originalPrice ||
                newIsNA !== cell.originalIsNA;

            if (
                newPrice === cell.currentPrice &&
                newIsNA === cell.isNA &&
                hasChanged === cell.hasChanged
            ) {
                return prev;
            }

            const newMap = new Map(prev);
            newMap.set(priceId, {
                ...cell,
                currentPrice: newPrice,
                isNA: newIsNA,
                hasChanged,
            });
            return newMap;
        });
    }, []);

    const reconcileSavedCells = useCallback(() => {
        setPricesMap(prev => {
            let didChange = false;
            const next = new Map(prev);
            next.forEach((cell, cellPriceId) => {
                if (cell.hasChanged) {
                    didChange = true;
                    next.set(cellPriceId, {
                        ...cell,
                        originalPrice: cell.currentPrice,
                        originalIsNA: cell.isNA,
                        hasChanged: false,
                    });
                }
            });
            return didChange ? next : prev;
        });
    }, []);

    const addCompetitorLocally = useCallback((competitor: Competitor) => {
        const surveyData = surveyRef.current;
        if (!surveyData?.id) return;

        const wasRemoved = competitorTrackingRef.current.pendingRemoves.has(
            competitor.id,
        );

        if (wasRemoved) {
            setCompetitorTracking(prev => {
                const newPendingRemoves = new Set(prev.pendingRemoves);
                newPendingRemoves.delete(competitor.id);
                const originalSc = surveyData.surveyCompetitors?.find(
                    sc => sc.competitorId === competitor.id,
                );
                return {
                    activeCompetitors: originalSc
                        ? [...prev.activeCompetitors, originalSc]
                        : prev.activeCompetitors,
                    pendingAdds: prev.pendingAdds,
                    pendingRemoves: newPendingRemoves,
                };
            });

            setPricesMap(prev => {
                const newMap = new Map(prev);
                const compLookup = new Map<string, PriceSurveyCompetitor>();
                surveyData.surveyCompetitors?.forEach(sc => {
                    compLookup.set(sc.id, sc);
                });

                surveyData.surveyItems?.forEach(item => {
                    item.surveyItemPrices?.forEach(sip => {
                        const sc = compLookup.get(
                            sip.priceSurveyCompetitorId || '',
                        );
                        if (
                            sc?.competitorId === competitor.id &&
                            sc?.competitor &&
                            sip.id
                        ) {
                            const isNA = sip.isNA ?? false;
                            const price = sip.price ?? null;
                            newMap.set(sip.id, {
                                priceId: sip.id,
                                surveyId: surveyData.id,
                                surveyItemId: item.id,
                                competitorId: competitor.id,
                                competitor: sc.competitor,
                                priceSurveyCompetitorId:
                                    sip.priceSurveyCompetitorId || '',
                                originalPrice: price,
                                currentPrice: price,
                                originalIsNA: isNA,
                                isNA,
                                hasChanged: false,
                            });
                        }
                    });
                });

                return newMap;
            });
        } else {
            const tempPscId = generateTempId('temp_psc');

            const tempPsc = {
                __typename: 'PriceSurveyCompetitor' as const,
                id: tempPscId,
                competitorId: competitor.id,
                competitor,
                surveyId: surveyData.id,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            } as PriceSurveyCompetitor;

            setCompetitorTracking(prev => {
                const newPendingAdds = new Set(prev.pendingAdds);
                newPendingAdds.add(competitor.id);
                return {
                    activeCompetitors: [...prev.activeCompetitors, tempPsc],
                    pendingAdds: newPendingAdds,
                    pendingRemoves: prev.pendingRemoves,
                };
            });

            setPricesMap(prev => {
                const newMap = new Map(prev);
                surveyData.surveyItems?.forEach(item => {
                    const tempPriceId = generateTempId(
                        `temp_price_${competitor.id}_${item.id}`,
                    );
                    newMap.set(tempPriceId, {
                        priceId: tempPriceId,
                        surveyId: surveyData.id,
                        surveyItemId: item.id,
                        competitorId: competitor.id,
                        competitor,
                        priceSurveyCompetitorId: tempPscId,
                        originalPrice: null,
                        currentPrice: null,
                        originalIsNA: false,
                        isNA: false,
                        hasChanged: false,
                    });
                });
                return newMap;
            });
        }
    }, []);

    const removeCompetitorLocally = useCallback((competitorId: string) => {
        const wasLocallyAdded =
            competitorTrackingRef.current.pendingAdds.has(competitorId);

        if (wasLocallyAdded) {
            setCompetitorTracking(prev => {
                const newPendingAdds = new Set(prev.pendingAdds);
                newPendingAdds.delete(competitorId);
                return {
                    activeCompetitors: prev.activeCompetitors.filter(
                        sc => sc.competitorId !== competitorId,
                    ),
                    pendingAdds: newPendingAdds,
                    pendingRemoves: prev.pendingRemoves,
                };
            });
        } else {
            setCompetitorTracking(prev => {
                const newPendingRemoves = new Set(prev.pendingRemoves);
                newPendingRemoves.add(competitorId);
                return {
                    activeCompetitors: prev.activeCompetitors.filter(
                        sc => sc.competitorId !== competitorId,
                    ),
                    pendingAdds: prev.pendingAdds,
                    pendingRemoves: newPendingRemoves,
                };
            });
        }

        setPricesMap(prev => {
            const newMap = new Map(prev);
            newMap.forEach((cell, priceId) => {
                if (cell.competitorId === competitorId) {
                    newMap.delete(priceId);
                }
            });
            return newMap;
        });
    }, []);

    const hasAnyChanges = useMemo(() => {
        if (competitorTracking.pendingAdds.size > 0) return true;
        if (competitorTracking.pendingRemoves.size > 0) return true;
        return Array.from(pricesMap.values()).some(cell => cell.hasChanged);
    }, [
        pricesMap,
        competitorTracking.pendingAdds,
        competitorTracking.pendingRemoves,
    ]);

    const changedCount = useMemo(() => {
        const priceChanges = Array.from(pricesMap.values()).filter(
            cell => cell.hasChanged,
        ).length;
        return (
            priceChanges +
            competitorTracking.pendingAdds.size +
            competitorTracking.pendingRemoves.size
        );
    }, [
        pricesMap,
        competitorTracking.pendingAdds,
        competitorTracking.pendingRemoves,
    ]);

    const saveChanges = useCallback((): Promise<boolean> => {
        if (saveInFlightRef.current) {
            return saveInFlightRef.current;
        }

        const runSave = async (): Promise<boolean> => {
            if (!survey?.id) {
                return false;
            }

            isSavingRef.current = true;
            setIsSaving(true);
            const { activeCompetitors, pendingAdds, pendingRemoves } =
                competitorTrackingRef.current;
            const hadCompetitorMutations =
                pendingAdds.size > 0 || pendingRemoves.size > 0;

            const currentPricesMap = pricesMapRef.current;
            const cellsByCompetitor = new Map<string, SurveyPriceCell[]>();
            currentPricesMap.forEach(cell => {
                const existing = cellsByCompetitor.get(cell.competitorId) || [];
                existing.push(cell);
                cellsByCompetitor.set(cell.competitorId, existing);
            });

            const competitors: Array<{
                competitorId: string;
                isNew: boolean;
                isRemoved: boolean;
                prices: Array<{
                    surveyItemId: string;
                    price: number | null;
                    isNA: boolean;
                }>;
            }> = [];

            activeCompetitors.forEach(sc => {
                const cells = cellsByCompetitor.get(sc.competitorId) || [];
                const isNew = pendingAdds.has(sc.competitorId);
                const hasChangedCells = cells.some(cell => cell.hasChanged);

                if (isNew || hasChangedCells) {
                    competitors.push({
                        competitorId: sc.competitorId,
                        isNew,
                        isRemoved: false,
                        prices: cells.map(cell => ({
                            surveyItemId: cell.surveyItemId,
                            price: cell.isNA ? null : cell.currentPrice,
                            isNA: cell.isNA,
                        })),
                    });
                }
            });

            pendingRemoves.forEach(competitorId => {
                competitors.push({
                    competitorId,
                    isNew: false,
                    isRemoved: true,
                    prices: [],
                });
            });

            if (competitors.length === 0) {
                isSavingRef.current = false;
                setIsSaving(false);
                await onSaveComplete?.();
                return true;
            }

            const totalChanges =
                competitors
                    .filter(c => !c.isRemoved)
                    .reduce((acc, c) => acc + c.prices.length, 0) +
                pendingRemoves.size;

            showLoadingNotification(
                SAVE_NOTIFICATION_ID,
                'Saving Progress',
                `Syncing ${totalChanges} change${
                    totalChanges === 1 ? '' : 's'
                }...`,
            );

            try {
                const result = await syncCompetitorPrices({
                    variables: {
                        input: {
                            surveyId: survey.id,
                            competitors,
                        },
                    },
                });

                if (result.data?.syncSurveyCompetitorPrices?.success) {
                    const {
                        competitorsAdded,
                        competitorsRemoved,
                        pricesUpdated,
                    } = result.data.syncSurveyCompetitorPrices;

                    const parts: string[] = [];
                    if (competitorsAdded > 0)
                        parts.push(
                            `${competitorsAdded} competitor${
                                competitorsAdded === 1 ? '' : 's'
                            } added`,
                        );
                    if (competitorsRemoved > 0)
                        parts.push(
                            `${competitorsRemoved} competitor${
                                competitorsRemoved === 1 ? '' : 's'
                            } removed`,
                        );
                    if (pricesUpdated > 0)
                        parts.push(
                            `${pricesUpdated} price${
                                pricesUpdated === 1 ? '' : 's'
                            } updated`,
                        );

                    updateSuccessNotification(
                        SAVE_NOTIFICATION_ID,
                        'Progress Saved',
                        parts.length > 0
                            ? parts.join(', ')
                            : 'All changes saved',
                    );

                    if (hadCompetitorMutations) {
                        reinitRequestedRef.current = true;
                    }
                    await onSaveComplete?.();
                    if (!hadCompetitorMutations) {
                        reconcileSavedCells();
                    }
                    return true;
                }

                updateFailedNotification(
                    SAVE_NOTIFICATION_ID,
                    'Save Failed',
                    'An error occurred while saving',
                );
                return false;
            } catch (error: unknown) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error';
                updateFailedNotification(
                    SAVE_NOTIFICATION_ID,
                    'Save Failed',
                    errorMessage,
                );
                return false;
            } finally {
                isSavingRef.current = false;
                setIsSaving(false);
            }
        };

        const savePromise = runSave();
        saveInFlightRef.current = savePromise;
        return savePromise.finally(() => {
            if (saveInFlightRef.current === savePromise) {
                saveInFlightRef.current = null;
            }
        });
    }, [survey?.id, syncCompetitorPrices, onSaveComplete, reconcileSavedCells]);

    return {
        pricesMap,
        activeCompetitors: competitorTracking.activeCompetitors,
        getCell,
        getCellByItemAndCompetitor,
        updateCell,
        addCompetitorLocally,
        removeCompetitorLocally,
        hasAnyChanges,
        changedCount,
        saveChanges,
        isSaving,
        initializePrices,
        requestReinit,
    };
}
